import { Controller } from "@hotwired/stimulus";

// Responsive navigation. Below the desktop breakpoint the links collapse behind
// a toggle that opens a panel; this controller manages the open/closed state and
// the accessibility around it: aria-expanded, close on Escape (returning focus to
// the toggle), close on outside click and on navigating a link, and close if the
// viewport grows back to desktop (where the panel is irrelevant).
export default class extends Controller {
  static targets = ["toggle", "menu"];

  connect() {
    this.close = this.close.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onClickOutside = this.onClickOutside.bind(this);
    // Complement of the header-collapse breakpoint in 07-responsive-breakpoints.css
    // (max-width: 1279px). The two MUST move together: if this fires while the CSS
    // still has the panel layout applied, the panel closes at a width where it is
    // the only navigation there is.
    this.desktop = window.matchMedia("(min-width: 1280px)");
    this.desktop.addEventListener("change", this.close);
    // Lets system specs wait until the toggle action is wired: a click before
    // connect never reaches the action and is a silent no-op.
    this.element.setAttribute("data-nav-ready", "");
  }

  disconnect() {
    this.desktop.removeEventListener("change", this.close);
    document.removeEventListener("keydown", this.onKeydown);
    document.removeEventListener("click", this.onClickOutside);
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this.element.classList.add("nav-open");
    this.toggleTarget.setAttribute("aria-expanded", "true");
    document.addEventListener("keydown", this.onKeydown);
    document.addEventListener("click", this.onClickOutside);
    this.menuTarget.querySelector("a")?.focus();
  }

  close() {
    if (!this.isOpen) {return;}
    this.element.classList.remove("nav-open");
    this.toggleTarget.setAttribute("aria-expanded", "false");
    document.removeEventListener("keydown", this.onKeydown);
    document.removeEventListener("click", this.onClickOutside);
  }

  get isOpen() {
    return this.element.classList.contains("nav-open");
  }

  onKeydown(event) {
    if (event.key === "Escape") {
      this.close();
      this.toggleTarget.focus();
    }
  }

  onClickOutside(event) {
    if (!this.element.contains(event.target)) {this.close();}
  }
}
